import { useEffect, useRef, useCallback } from 'react';

interface SpatialNavigationOptions {
  onEnter?: () => void;
  onArrowUp?: () => void;
  onArrowDown?: () => void;
  onArrowLeft?: () => void;
  onArrowRight?: () => void;
  onBack?: () => void;
  onPlay?: () => void;
  onPause?: () => void;
  onStop?: () => void;
}

export const useSpatialNavigation = (options: SpatialNavigationOptions) => {
  const elementRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    // Prevent default behavior for navigation keys
    const navigationKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter', 'Backspace', 'Escape'];
    
    if (navigationKeys.includes(event.key)) {
      event.preventDefault();
    }

    switch (event.key) {
      case 'Enter':
      case ' ':
        options.onEnter?.();
        break;
      case 'ArrowUp':
        options.onArrowUp?.();
        break;
      case 'ArrowDown':
        options.onArrowDown?.();
        break;
      case 'ArrowLeft':
        options.onArrowLeft?.();
        break;
      case 'ArrowRight':
        options.onArrowRight?.();
        break;
      case 'Backspace':
      case 'Escape':
        options.onBack?.();
        break;
      case 'MediaPlayPause':
        options.onPlay?.();
        break;
      case 'MediaStop':
        options.onStop?.();
        break;
    }
  }, [options]);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    element.addEventListener('keydown', handleKeyDown);
    element.setAttribute('tabindex', '0');
    element.focus();

    return () => {
      element.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  return elementRef;
};

// Hook for managing focus between elements
export const useFocusManagement = () => {
  const focusableElements = useRef<HTMLElement[]>([]);
  const currentFocusIndex = useRef(0);

  const updateFocusableElements = useCallback(() => {
    const elements = document.querySelectorAll(
      'button, [tabindex]:not([tabindex="-1"]), input, select, textarea, a[href]'
    ) as NodeListOf<HTMLElement>;
    
    focusableElements.current = Array.from(elements);
  }, []);

  const focusNext = useCallback(() => {
    updateFocusableElements();
    if (focusableElements.current.length === 0) return;

    currentFocusIndex.current = (currentFocusIndex.current + 1) % focusableElements.current.length;
    focusableElements.current[currentFocusIndex.current]?.focus();
  }, [updateFocusableElements]);

  const focusPrevious = useCallback(() => {
    updateFocusableElements();
    if (focusableElements.current.length === 0) return;

    currentFocusIndex.current = currentFocusIndex.current === 0 
      ? focusableElements.current.length - 1 
      : currentFocusIndex.current - 1;
    focusableElements.current[currentFocusIndex.current]?.focus();
  }, [updateFocusableElements]);

  const focusFirst = useCallback(() => {
    updateFocusableElements();
    if (focusableElements.current.length > 0) {
      currentFocusIndex.current = 0;
      focusableElements.current[0]?.focus();
    }
  }, [updateFocusableElements]);

  useEffect(() => {
    updateFocusableElements();
  }, [updateFocusableElements]);

  return {
    focusNext,
    focusPrevious,
    focusFirst,
    updateFocusableElements
  };
};
